import { isOnline, showOfflineBanner } from "../states/connection";
import { Config } from "../config/store";
import * as TestWords from "./test-words";
import { getCurrentInput } from "./events/data";
import { getLiveCachedAccuracy } from "./events/live-cache";
import * as Caret from "./caret";
import * as Misc from "../utils/misc";
import * as ResultWordHighlight from "../components/pages/test/result/result-word-highlight";
import { ModifierKeys } from "../constants/modifier-keys";
import { isDevEnvironment } from "../utils/env";
import * as SoundController from "../controllers/sound-controller";
import * as Numbers from "@monkeytype/util/numbers";
import {
  blurInputElement,
  focusInputElement,
  isInputElementFocused,
} from "../input/input-element";
import * as MonkeyPower from "../elements/monkey-power";
import * as AdController from "../controllers/ad-controller";
import * as ThemeController from "../controllers/theme-controller";
import { ElementWithUtils } from "../utils/dom";
import {
  replaceWords,
  setActiveWord,
  setWordTyped,
  setWordInput,
  truncateWords,
} from "../states/words-render";
import { skipBreakdownEvent } from "../states/header";
import {
  isDirectionReversed,
  isRtlCheckedByLetter,
  getActiveWordIndex,
  isTestActive,
  setCurrentLiveStats,
  setTestFocusState,
  isTestFinished,
  setLayoutfluidTimerVisible,
  setTestInitError,
  setTestInitFailed,
  setTypingTestHidden,
  getCompositionData,
  highlightKeymapKey,
  setWordsInputPosition,
  triggerScrollWindowToActiveWord,
  setFocusMode,
} from "../states/test";
import { untrack } from "solid-js";
import { setResultShown } from "../states/result";
import { isTestPageMounted, markTestPageMounted } from "./test-page-state";

// rendered by TestPage, registered via init() on every mount
let typingTestRef: ElementWithUtils | undefined;
// rendered by TestResult, registered while it's mounted
let resultRef: ElementWithUtils | undefined;

export function setResultElement(el: ElementWithUtils | undefined): void {
  resultRef = el;
}

/** Whether TestPage is mounted. Not reactive. */
export function isMounted(): boolean {
  return untrack(isTestPageMounted);
}

function typingTestEl(): ElementWithUtils {
  if (typingTestRef === undefined) throw new Error("#typingTest not mounted");
  return typingTestRef;
}

export function focusWords(force = false): void {
  if (force) {
    blurInputElement();
  }
  focusInputElement(true);
  if (isTestActive()) {
    triggerScrollWindowToActiveWord();
  } else {
    Misc.scrollToCenterOrTop(typingTestEl().native);
  }
}

// buffered, rendered in the next words frame
function updateWordLetters({
  wordIndex,
  input,
  compositionData,
}: {
  wordIndex: number;
  input: string;
  compositionData: string;
}): void {
  setWordInput(wordIndex, input, compositionData);
}

// this means input, delete or composition
function afterAnyTestInput(
  type: "textInput" | "delete" | "compositionUpdate",
  correctInput: boolean | null,
): void {
  if (type === "textInput" || type === "compositionUpdate") {
    if (
      correctInput === true ||
      Config.playSoundOnError === "off" ||
      Config.blindMode
    ) {
      void SoundController.playClick();
    } else {
      void SoundController.playError();
    }
  } else if (type === "delete") {
    void SoundController.playClick();
  }

  const acc = Numbers.roundTo2(getLiveCachedAccuracy() ?? 100);
  if (!isNaN(acc)) {
    setCurrentLiveStats({ acc });
  }

  if (Config.keymapMode === "next") {
    const keyToHighlight =
      TestWords.words.getCurrent()?.textWithCommit[getCurrentInput().length];
    if (keyToHighlight !== undefined) {
      highlightKeymapKey(keyToHighlight);
    }
  }

  setFocusMode(true);
  Caret.stopAnimation();
}

export function afterTestTextInput(
  correct: boolean,
  inputOverride?: string,
  goingToNextWord = false,
): void {
  void MonkeyPower.addPower(correct);

  let input = inputOverride ?? getCurrentInput();
  if (goingToNextWord) {
    input = input.replace(/ $/, "");
  }

  updateWordLetters({
    input,
    wordIndex: getActiveWordIndex(),
    compositionData: getCompositionData(),
  });

  afterAnyTestInput("textInput", correct);
}

export function afterTestCompositionUpdate(): void {
  updateWordLetters({
    input: getCurrentInput(),
    wordIndex: getActiveWordIndex(),
    compositionData: getCompositionData(),
  });
  // correct needs to be true to get the normal click sound
  afterAnyTestInput("compositionUpdate", true);
}

export function afterTestDelete(): void {
  updateWordLetters({
    input: getCurrentInput(),
    wordIndex: getActiveWordIndex(),
    compositionData: getCompositionData(),
  });
  afterAnyTestInput("delete", null);
}

export function beforeTestWordChange(direction: "forward" | "back"): void {
  if (direction === "back") {
    updateWordLetters({
      input: getCurrentInput(),
      wordIndex: getActiveWordIndex(),
      compositionData: getCompositionData(),
    });
  }
}

export async function afterTestWordChange(
  direction: "forward" | "back",
  lastBurst?: number | null,
): Promise<void> {
  const activeWordIndex = getActiveWordIndex();
  if (direction === "forward") setWordTyped(activeWordIndex - 1, true);
  setWordTyped(activeWordIndex, false);
  setActiveWord(activeWordIndex, direction);

  if (lastBurst !== null && Numbers.isSafeNumber(lastBurst)) {
    setCurrentLiveStats({ burst: Math.round(lastBurst) });
  }

  if (Config.keymapMode === "next") {
    const keyToHighlight =
      TestWords.words.getCurrent()?.textWithCommit[getCurrentInput().length];
    if (keyToHighlight !== undefined) {
      highlightKeymapKey(keyToHighlight);
    }
  }

  if (direction === "back" && Config.mode === "zen") {
    truncateWords(activeWordIndex + 1);
  }
}

export function onTestStart(): void {
  setFocusMode(true);
  setCurrentLiveStats({
    wpm: 0,
    acc: 100,
    raw: 0,
    burst: 0,
    seconds: 0,
  });
}

function getRestartAnimationTime(noAnim: boolean): number {
  return noAnim ? 0 : Misc.applyReducedMotion(125);
}

export async function fadeOutTest(
  source: "testPage" | "resultPage",
  noAnim: boolean,
): Promise<void> {
  const el = source === "resultPage" ? resultRef : typingTestEl();
  await el?.promiseAnimate({
    opacity: 0,
    duration: getRestartAnimationTime(noAnim),
  });
}

export async function fadeInTest(noAnim: boolean): Promise<void> {
  await typingTestEl().promiseAnimate({
    opacity: [0, 1],
    duration: getRestartAnimationTime(noAnim),
  });
}

export function onTestRestart(source: "testPage" | "resultPage"): void {
  setResultShown(false);
  typingTestEl().setStyle({ opacity: "0" });
  setTypingTestHidden(false);
  setWordsInputPosition((position) => ({ ...position, left: 0 }));
  setFocusMode(false);
  setCurrentLiveStats({
    wpm: undefined,
    acc: undefined,
    raw: undefined,
    burst: undefined,
    seconds: undefined,
  });
  setLayoutfluidTimerVisible(false);
  ResultWordHighlight.destroy();
  MonkeyPower.reset();
  Caret.resetPosition();
  setTestInitFailed(false);
  setTestInitError(null);
  focusWords(true);

  if (!isOnline()) {
    showOfflineBanner();
  }

  if (source === "resultPage") {
    if (Config.randomTheme !== "off") {
      void ThemeController.randomizeTheme();
    }
    skipBreakdownEvent.dispatch();
  }

  AdController.destroyResult();
  void SoundController.clearAllSounds();
  replaceWords(
    Config.mode === "zen"
      ? [""]
      : TestWords.words.get().map((word) => word.display),
    {
      activeWordIndex: getActiveWordIndex(),
      zen: Config.mode === "zen",
      marksRtlWords: !isRtlCheckedByLetter() && !isDirectionReversed(),
    },
  );
}

export function onTestFinish(): void {
  Caret.hide();
  setTestFocusState("focused");
  if (Config.playSoundOnClick === "16") {
    void SoundController.playFartReverb();
  }
}

/**
 * Binds test-ui to TestPage's elements. Call on every mount; refs are
 * released when `signal` aborts.
 */
export function init(
  refs: {
    typingTest: ElementWithUtils;
  },
  signal: AbortSignal,
): void {
  typingTestRef = refs.typingTest;
  signal.addEventListener("abort", () => {
    // a remount may have registered new elements already
    if (typingTestRef === refs.typingTest) typingTestRef = undefined;
  });
  markTestPageMounted(signal);

  // autofocus the words when typing anywhere on the page
  document.addEventListener("keydown", autofocusWords, { signal });
}

function autofocusWords(e: KeyboardEvent): void {
  if (e.key === undefined) return;

  if (isDevEnvironment()) {
    if (
      (document.activeElement as HTMLElement | undefined)?.dataset[
        "uiElement"
      ] === "signalDevtoolsInput"
    ) {
      return;
    }
  }

  if (isTestFinished() || isInputElementFocused()) return;
  // checked last, isAnyPopupVisible is a bit expensive
  if (
    ["Enter", " ", "Escape", "Tab", ...ModifierKeys].includes(e.key) ||
    e.metaKey ||
    e.ctrlKey ||
    Misc.isAnyPopupVisible()
  ) {
    return;
  }
  focusWords();
  if (Config.showOutOfFocusWarning) {
    e.preventDefault();
  }
}
